import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, RotateCcw, Trash2, FileDown, Loader2, Check, X, Inbox, RefreshCw } from 'lucide-react';
import { RESTORE_COUNT_EVENT } from '../../hooks/useRestoreRequestCount';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel,
} from '../ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'requests', label: 'Restore requests' },
  { value: 'restored', label: 'Restored' },
  { value: 'purged', label: 'Purged' },
];

const BILLING_BADGE = {
  none: { label: 'No subscription', cls: 'bg-gray-100 hover:bg-gray-100 text-gray-600' },
  paused: { label: 'Paused (renewal off)', cls: 'bg-amber-100 hover:bg-amber-100 text-amber-800' },
  resumed: { label: 'Resumed', cls: 'bg-emerald-100 hover:bg-emerald-100 text-emerald-800' },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-200 hover:bg-slate-200 text-slate-700' },
  needs_attention: { label: 'Needs attention', cls: 'bg-red-600 hover:bg-red-600 text-white' },
};

const timeLeft = (iso) => {
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return 'Purging soon';
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  return d > 0 ? `${d}d ${h}h left` : `${h}h ${Math.floor((ms % 3600000) / 60000)}m left`;
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

export const downloadReceipt = async (id, isBatch = false) => {
  try {
    const path = isBatch ? `deletion-batches/${id}` : `deletions/${id}`;
    const res = await axios.get(`${API}/admin/${path}/receipt`, { withCredentials: true, responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `deletion-receipt-${id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error('Receipt download failed');
  }
};

const BillingCell = ({ rec, busy, onRetry }) => {
  const b = rec.billing_status || { state: 'none' };
  const badge = BILLING_BADGE[b.state] || BILLING_BADGE.none;
  const endLabel = b.state === 'cancelled' ? 'Ended' : b.state === 'paused' ? 'Ends' : 'Renews';
  return (
    <td className="py-2.5 pr-4" data-testid={`billing-status-${rec.deletion_id}`}>
      <Badge className={`${badge.cls} whitespace-nowrap`} title={b.error || undefined}
        data-testid={`billing-badge-${rec.deletion_id}`}>{badge.label}</Badge>
      {b.state === 'needs_attention' && (
        <>
          {b.error && <p className="text-[11px] text-red-600 mt-0.5 max-w-[220px] truncate" title={b.error}>{b.error}</p>}
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onRetry(rec)}
            className="mt-1 h-7 px-2 text-xs text-red-700 border-red-200 hover:bg-red-50" data-testid={`retry-billing-${rec.deletion_id}`}>
            <RefreshCw className={`w-3 h-3 mr-1 ${busy ? 'animate-spin' : ''}`} />Retry
          </Button>
        </>
      )}
      {b.plan && (
        <p className="text-xs text-gray-500 mt-0.5">{b.plan.replace(/_/g, ' ')}{b.period_end && ` · ${endLabel} ${fmt(b.period_end)}`}</p>
      )}
    </td>
  );
};

const RowActions = ({ rec, view, busy, confirmPurge, onRestore, onPurge, onDecline }) => (
  <td className="py-2.5 text-right whitespace-nowrap space-x-1.5">
    {view === 'requests' ? (
      <>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500" disabled={busy} onClick={() => onRestore(rec)}
          data-testid={`approve-restore-${rec.deletion_id}`}><Check className="w-3.5 h-3.5 mr-1" />Approve</Button>
        <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" disabled={busy} onClick={() => onDecline(rec)}
          data-testid={`decline-restore-${rec.deletion_id}`}><X className="w-3.5 h-3.5 mr-1" />Decline</Button>
      </>
    ) : rec.status === 'pending' && (
      <>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onRestore(rec)} data-testid={`restore-user-${rec.deletion_id}`}>
          <RotateCcw className="w-3.5 h-3.5 mr-1" />Restore
        </Button>
        <Button size="sm" variant={confirmPurge === rec.deletion_id ? 'destructive' : 'outline'} disabled={busy}
          className={confirmPurge === rec.deletion_id ? '' : 'text-red-600 hover:text-red-700'}
          onClick={() => onPurge(rec)} data-testid={`purge-now-${rec.deletion_id}`}>
          <Trash2 className="w-3.5 h-3.5 mr-1" />{confirmPurge === rec.deletion_id ? 'Confirm' : 'Delete now'}
        </Button>
      </>
    )}
    <Button size="sm" variant="ghost" onClick={() => downloadReceipt(rec.deletion_id)} data-testid={`receipt-${rec.deletion_id}`}>
      <FileDown className="w-3.5 h-3.5 mr-1" />Receipt
    </Button>
    {rec.batch_id && (
      <Button size="sm" variant="ghost" onClick={() => downloadReceipt(rec.batch_id, true)} data-testid={`batch-receipt-${rec.deletion_id}`}>
        <FileDown className="w-3.5 h-3.5 mr-1" />Batch
      </Button>
    )}
  </td>
);

const RequestNote = ({ rec }) => {
  const rr = rec.restore_request;
  if (!rr || rec.status !== 'pending') return null;
  if (rr.decision === 'declined') {
    return <Badge variant="secondary" className="mt-1" data-testid={`restore-declined-badge-${rec.deletion_id}`}>Restore declined {fmt(rr.decided_at)}</Badge>;
  }
  return (
    <Badge className="mt-1 bg-amber-100 text-amber-800 hover:bg-amber-100 max-w-xs whitespace-normal text-left font-normal"
      title={rr.message} data-testid={`restore-request-badge-${rec.deletion_id}`}>
      Restore requested {fmt(rr.requested_at)}{rr.message && `: “${rr.message.slice(0, 120)}”`}
    </Badge>
  );
};

const DeletionRow = ({ rec, ...rest }) => (
  <tr className="border-b last:border-0" data-testid={`deletion-row-${rec.deletion_id}`}>
    <td className="py-2.5 pr-4">
      <p className="text-sm font-medium">{rec.user_email}</p>
      <p className="text-xs text-gray-500 font-mono">{rec.deletion_id}{rec.batch_id && ` · ${rec.batch_id}`}</p>
      <RequestNote rec={rec} />
    </td>
    <td className="py-2.5 pr-4 text-sm">{rec.deleted_by_email}<p className="text-xs text-gray-500">{fmt(rec.requested_at)}</p></td>
    <td className="py-2.5 pr-4 text-sm">
      {rec.status === 'pending' ? (
        <>
          {fmt(rec.purge_after)}
          <p className="text-xs text-amber-600 font-medium flex items-center gap-1" data-testid={`deletion-countdown-${rec.deletion_id}`}>
            <Clock className="w-3 h-3" />{timeLeft(rec.purge_after)}
          </p>
        </>
      ) : (
        <Badge variant={rec.status === 'restored' ? 'secondary' : 'destructive'}>
          {rec.status === 'restored' ? `Restored ${fmt(rec.restored_at)}` : `Purged ${fmt(rec.purged_at)}`}
        </Badge>
      )}
    </td>
    <BillingCell rec={rec} busy={rest.busy} onRetry={rest.onRetry} />
    <RowActions rec={rec} {...rest} />
  </tr>
);

const DeclineDialog = ({ rec, busy, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  return (
    <AlertDialog open={!!rec} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="decline-restore-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Decline restore request?</AlertDialogTitle>
          <AlertDialogDescription>
            {rec?.user_email} stays scheduled for deletion and will be emailed. They can't send another request.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} rows={3}
          placeholder="Reason shared with the user (optional)" data-testid="decline-restore-reason" />
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="decline-restore-cancel">Cancel</AlertDialogCancel>
          <Button variant="destructive" disabled={busy} onClick={() => onConfirm(reason)} data-testid="decline-restore-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Decline request'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const PendingDeletions = ({ refreshKey, onChanged }) => {
  const [view, setView] = useState('pending');
  const [records, setRecords] = useState(null);
  const [openRequests, setOpenRequests] = useState(0);
  const [busy, setBusy] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(null);
  const [declining, setDeclining] = useState(null);
  const confirmTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/deletions`, { params: { status: view }, withCredentials: true });
      setRecords(res.data.deletions);
      setOpenRequests(res.data.open_restore_requests || 0);
    } catch {
      setRecords([]);
    }
  }, [view]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const post = async (url, body = {}) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/deletions/${url}`, body, { withCredentials: true });
      toast.success(res.data.message);
      await load();
      onChanged();
      window.dispatchEvent(new Event(RESTORE_COUNT_EVENT));
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
      load();
      return false;
    } finally {
      setBusy(false);
      setConfirmPurge(null);
    }
  };

  const onPurge = (rec) => {
    clearTimeout(confirmTimer.current);
    if (confirmPurge !== rec.deletion_id) {
      setConfirmPurge(rec.deletion_id);
      confirmTimer.current = setTimeout(() => setConfirmPurge(null), 4000);
      return;
    }
    post(`${rec.deletion_id}/purge`);
  };

  const confirmDecline = async (reason) => {
    if (await post(`${declining.deletion_id}/decline-restore`, { reason })) setDeclining(null);
  };

  return (
    <Card className="mb-4 border-amber-200" data-testid="pending-deletions-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0 pb-3">
        <div>
          <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" />Pending deletions</CardTitle>
          <CardDescription>Users are locked out right away and permanently purged after 14 days. Restore anytime before then.</CardDescription>
        </div>
        <div className="flex gap-1">
          {STATUS_TABS.map((t) => (
            <Button key={t.value} size="sm" variant={view === t.value ? 'default' : 'ghost'}
              onClick={() => { if (t.value !== view) { setRecords(null); setView(t.value); } }}
              data-testid={`deletions-filter-${t.value}`}>
              {t.value === 'requests' && <Inbox className="w-3.5 h-3.5 mr-1" />}{t.label}
              {t.value === 'requests' && openRequests > 0 && (
                <span className="ml-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5"
                  data-testid="open-restore-requests-count">{openRequests}</span>
              )}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {records === null ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : records.length === 0 ? (
          <p className="text-sm text-gray-500 py-3" data-testid="deletions-empty">
            {view === 'requests' ? 'No open restore requests.' : `No ${view} deletions.`}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-4 font-medium">User</th><th className="py-2 pr-4 font-medium">Deleted by</th>
                  <th className="py-2 pr-4 font-medium">Purge date</th><th className="py-2 pr-4 font-medium">Billing</th>
                  <th className="py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <DeletionRow key={rec.deletion_id} rec={rec} view={view} busy={busy} confirmPurge={confirmPurge}
                    onRestore={(r) => post(`${r.deletion_id}/restore`)} onPurge={onPurge} onDecline={setDeclining}
                    onRetry={(r) => post(`${r.deletion_id}/retry-billing`)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      <DeclineDialog key={declining?.deletion_id || 'none'} rec={declining} busy={busy}
        onClose={() => setDeclining(null)} onConfirm={confirmDecline} />
    </Card>
  );
};

export default PendingDeletions;
